import {
    Box,
    Flex,
    HStack,
    Text,
    VStack,
} from '@chakra-ui/react';
import {
    InventoryActivityLogEntry,
    InventoryActivityLogChange,
} from '../../types/inventory';
import { parseInventoryActivityChanges } from '../../hooks/inventory/useGetInventoryActivityLogs';
import { formatPrice } from '../../functions';
import { formatInventoryPartTypeLabel } from '../../utils/inventoryConstants';
import { brandCopy } from '../../constants/brand';
import { Icon, type IconName } from '../../ui';

export const INVENTORY_EVENT_TYPE_LABELS: Record<string, string> = {
    'item-added': 'Item Added',
    'item-edited': 'Item Edited',
    'item-quantity-used': 'Quantity Used',
    'item-quantity-ordered': 'Quantity Ordered',
    'item-quantity-sold': 'Quantity Sold',
    'item-flagged': 'Item Flagged',
    'item-discontinued': 'Item Discontinued',
    'item-deleted': 'Item Deleted',
    'inventory-imported': 'Inventory Imported',
    'inventory-exported': 'Inventory Exported',
    'item-stock-adjustment-note-changed': 'Stock Adjustment Note',
    'item-stock-adjustment-deleted': 'Stock Adjustment Deleted',
};

export const INVENTORY_EVENT_TYPE_COLORS: Record<string, string> = {
    'item-added': 'info.fg',
    'item-edited': 'brand.solid',
    'item-quantity-used': 'warning.fg',
    'item-quantity-ordered': 'success.fg',
    'item-quantity-sold': 'info.fg',
    'item-flagged': 'warning.fg',
    'item-discontinued': 'fg.muted',
    'item-deleted': 'error.fg',
    'inventory-imported': 'accent.fg',
    'inventory-exported': 'accent.fg',
    'item-stock-adjustment-note-changed': 'brand.solid',
    'item-stock-adjustment-deleted': 'error.fg',
};

export const INVENTORY_EVENT_TYPE_ICON: Record<string, IconName> = {
    'item-added': 'plus',
    'item-edited': 'edit',
    'item-quantity-used': 'flag',
    'item-quantity-ordered': 'plus',
    'item-quantity-sold': 'plus',
    'item-flagged': 'flag',
    'item-discontinued': 'archive',
    'item-deleted': 'trash',
    'inventory-imported': 'upload',
    'inventory-exported': 'fileExport',
    'item-stock-adjustment-note-changed': 'edit',
    'item-stock-adjustment-deleted': 'trash',
};

const FIELD_LABELS: Record<string, string> = {
    name: 'Name',
    description: 'Name',
    'manufacturer-part-number': 'Mfg. Part Number',
    category: 'Category',
    'part-type': 'Part Type',
    'supplier-name': 'Supplier',
    'supplier-id': 'Supplier',
    unit: 'Unit',
    'purchase-unit': 'Unit',
    'reorder-quantity': 'Reorder Quantity',
    'target-quantity': 'Target Quantity',
    'unit-cost': 'Unit Cost',
    'in-stock-quantity': 'In-Stock Qty',
    status: 'Status',
    'business-line': 'Business Line',
    scope: 'Business Line',
    barcode: 'Barcode',
};

function formatFieldLabel(field: string): string {
    return FIELD_LABELS[field] ?? field.replace(/-/g, ' ');
}

const SUPPLIER_ACTIVITY_FIELDS = new Set(['supplier-name', 'supplier-id']);
const OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;

export function formatActivityChangeValue(
    field: string,
    value: string | undefined,
): string | undefined {
    if (value === undefined) return undefined;
    if (SUPPLIER_ACTIVITY_FIELDS.has(field) && OBJECT_ID_PATTERN.test(value)) {
        return 'Unknown supplier';
    }
    if (field === 'unit-cost') {
        const normalized = value.replace(/,/g, '').replace(/^\$/, '');
        const n = parseFloat(normalized);
        if (!Number.isNaN(n)) {
            return formatPrice(n);
        }
    }
    if (field === 'part-type') {
        return formatInventoryPartTypeLabel(value) || value;
    }
    return value;
}

export function formatInventoryActivityTime(ts: string): string {
    const d = new Date(ts);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const time = d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
    return `${month}/${day} • ${time}`;
}

export function getInventoryActorName(log: InventoryActivityLogEntry): string {
    if (log.userName) {
        return `${log.userName.firstName} ${log.userName.lastName}`.trim();
    }
    if (
        log.eventType === 'item-added' ||
        log.eventType === 'item-flagged' ||
        log.eventType === 'item-quantity-used'
    ) {
        return brandCopy('PartBay');
    }
    return 'System';
}

function renderChanges(changes: InventoryActivityLogChange[]) {
    if (changes.length === 0) return null;
    return (
        <VStack align="stretch" spacing={1} mt={1}>
            {changes.map((change, idx) => {
                const before = formatActivityChangeValue(change.field, change.before);
                const after = formatActivityChangeValue(change.field, change.after);
                return (
                    <Text key={idx} fontSize="xs" color="gray.600">
                        {formatFieldLabel(change.field)}
                        {before !== undefined && after !== undefined
                            ? `: ${before} → ${after}`
                            : after !== undefined
                              ? `: ${after}`
                              : ''}
                    </Text>
                );
            })}
        </VStack>
    );
}

function renderStockAdjustmentNoteChange(metadata: Record<string, string> | undefined) {
    if (!metadata) return null;
    const before = metadata.before;
    const after = metadata.after;
    if (before === undefined && after === undefined) return null;

    let detail: string;
    if (before !== undefined && after !== undefined) {
        detail = `Note: ${before} → ${after}`;
    } else if (after !== undefined) {
        detail = `Note: ${after}`;
    } else {
        detail = `Note: ${before} → (cleared)`;
    }

    return (
        <Text fontSize="xs" color="gray.600" mt={0.5}>
            {detail}
        </Text>
    );
}

const STOCK_ADJUSTMENT_TYPE_LABELS: Record<string, string> = {
    order: 'Order',
    usage: 'Usage',
    sale: 'Sale',
    initial: 'Initial',
};

function renderStockAdjustmentDeletedType(metadata: Record<string, string> | undefined) {
    if (!metadata?.type) return null;
    const typeLabel = STOCK_ADJUSTMENT_TYPE_LABELS[metadata.type] ?? metadata.type;
    return (
        <Text textStyle="caption" color="fg.muted" mt={0.5}>
            Type: {typeLabel}
        </Text>
    );
}

export function groupInventoryLogsByDate(
    logs: InventoryActivityLogEntry[],
): Map<string, InventoryActivityLogEntry[]> {
    const grouped = new Map<string, InventoryActivityLogEntry[]>();
    for (const log of logs) {
        const date = new Date(log.timestamp);
        const label = date.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
        const existing = grouped.get(label) ?? [];
        existing.push(log);
        grouped.set(label, existing);
    }
    return grouped;
}

interface RenderInventoryLogCardOptions {
    showItemRef?: boolean;
    flat?: boolean;
    onItemClick?: (inventoryItemId: string) => void;
    onImportActivityClick?: (jobId: string) => void;
    onExportActivityClick?: (exportId: string) => void;
}

export function renderInventoryLogCard(
    log: InventoryActivityLogEntry,
    options: RenderInventoryLogCardOptions = {},
) {
    const {
        showItemRef = false,
        flat = false,
        onItemClick,
        onImportActivityClick,
        onExportActivityClick,
    } = options;
    const displayType = log.eventType;
    const color = INVENTORY_EVENT_TYPE_COLORS[displayType] ?? 'brand.solid';
    const iconName: IconName = INVENTORY_EVENT_TYPE_ICON[displayType] ?? 'fileText';
    const changes = parseInventoryActivityChanges(log.metadata);
    const actionLabel = INVENTORY_EVENT_TYPE_LABELS[displayType] ?? displayType;
    const actorName = getInventoryActorName(log);
    const delta = log.metadata?.delta;

    const isImportEvent = displayType === 'inventory-imported';
    const isExportEvent = displayType === 'inventory-exported';
    const isOrgWideEvent = isImportEvent || isExportEvent;
    const importJobId = log.metadata?.jobId;
    const exportId = log.metadata?.exportId;
    const metadataSummary = isImportEvent
        ? `${log.metadata?.createdCount ?? '0'} item${log.metadata?.createdCount === '1' ? '' : 's'} imported`
        : isExportEvent
          ? `${log.metadata?.rowCount ?? '0'} item${log.metadata?.rowCount === '1' ? '' : 's'} exported`
          : undefined;
    const handleActivityClick =
        isImportEvent && onImportActivityClick && importJobId
            ? () => onImportActivityClick(importJobId)
            : isExportEvent && onExportActivityClick && exportId
              ? () => onExportActivityClick(exportId)
              : undefined;

    const cardProps = flat
        ? { bg: 'white', py: 3, borderBottom: '1px solid', borderColor: 'gray.100' as const }
        : {
              borderWidth: '1px',
              borderColor: 'gray.200' as const,
              borderRadius: 'lg',
              bg: 'white',
              p: 3,
          };

    return (
        <Box
            {...cardProps}
            cursor={handleActivityClick ? 'pointer' : undefined}
            _hover={handleActivityClick ? { bg: 'gray.50' } : undefined}
            onClick={handleActivityClick}
        >
            <Flex align="flex-start" gap={3}>
                <Flex
                    w="32px"
                    h="32px"
                    borderRadius="full"
                    bg="gray.100"
                    align="center"
                    justify="center"
                    flexShrink={0}
                >
                    <Icon name={iconName} size="sm" color={color} />
                </Flex>
                <Box flex={1} minW={0}>
                    <Flex align="center" justify="space-between" gap={2}>
                        <HStack spacing={1.5} minW={0}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                {actionLabel}
                            </Text>
                            <Text fontSize="sm" color="gray.400">
                                ·
                            </Text>
                            <Text fontSize="sm" color="gray.600" isTruncated>
                                {actorName}
                            </Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500" flexShrink={0}>
                            {formatInventoryActivityTime(log.timestamp)}
                        </Text>
                    </Flex>

                    {showItemRef && log.itemRef && !isOrgWideEvent && (
                        <Text
                            fontSize="xs"
                            color={log.eventType === 'item-deleted' ? 'gray.600' : 'brand.solid'}
                            fontWeight={log.eventType === 'item-deleted' ? 'normal' : 'medium'}
                            mt={0.5}
                            cursor={
                                onItemClick && log.eventType !== 'item-deleted'
                                    ? 'pointer'
                                    : undefined
                            }
                            _hover={
                                onItemClick && log.eventType !== 'item-deleted'
                                    ? { textDecoration: 'underline' }
                                    : undefined
                            }
                            onClick={
                                onItemClick && log.eventType !== 'item-deleted'
                                    ? () => onItemClick(log.itemRef!.inventoryItemId)
                                    : undefined
                            }
                        >
                            {log.itemRef.manufacturerPartNumber}
                            {log.itemRef.name ? ` — ${log.itemRef.name}` : ''}
                        </Text>
                    )}

                    {metadataSummary && (
                        <Text
                            fontSize="xs"
                            color={handleActivityClick ? 'brand.solid' : 'gray.600'}
                            fontWeight={handleActivityClick ? 'medium' : 'normal'}
                            mt={0.5}
                            _hover={handleActivityClick ? { textDecoration: 'underline' } : undefined}
                        >
                            {metadataSummary}
                        </Text>
                    )}

                    {displayType === 'item-stock-adjustment-deleted' &&
                        renderStockAdjustmentDeletedType(log.metadata)}

                    {log.metadata?.roNumber && (
                        <Text fontSize="xs" color="gray.600" mt={0.5}>
                            RO# {log.metadata.roNumber}
                        </Text>
                    )}

                    {delta !== undefined && delta !== '' && (
                        <Text fontSize="xs" color="gray.600" mt={0.5}>
                            Delta: {delta}
                        </Text>
                    )}

                    {displayType === 'item-stock-adjustment-note-changed' &&
                        renderStockAdjustmentNoteChange(log.metadata)}

                    {renderChanges(changes)}
                </Box>
            </Flex>
        </Box>
    );
}
